import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  User,  
  Shield, 
  FileText, 
  Mail, 
  LogOut,
  ChevronDown,
  Compass,
  HelpCircle,
  Star,
  Gem,
  BadgeCheck,
  BookOpen,
  LogIn,
  UserPlus,
  Menu,
  X
} from "lucide-react";

import { useUserContext } from "@/context/AuthContext";
import { useSignOutAccount } from "@/lib/react-query/queries";
import NotificationDropdown from "../shared/NotificationDropdown";
import { getUserTrustScore } from "@/lib/appwrite/trustScore";
import { buildSignInRedirect } from "@/lib/auth/redirect";
import { formatLoginIdentifier } from "@/lib/auth/loginIdentifier";

const Topbar = ({ scrollDir }: { scrollDir: "up" | "down" }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useUserContext();
  const { mutate: signOut, isSuccess } = useSignOutAccount();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [trustScore, setTrustScore] = useState<number | null>(null);
  const [level, setLevel] = useState<string>('bronze');

  useEffect(() => {
    if (isSuccess) navigate(0);
  }, [isSuccess]);

  // Fetch trust score data when user changes (only if authenticated)
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      const fetchTrustScoreData = async () => {
        try {
          const scoreData = await getUserTrustScore(user.id);
          setTrustScore(scoreData.score);
          setLevel(scoreData.level);
        } catch (error) {
          console.error('Error fetching trust score:', error);
        }
      };
      
      fetchTrustScoreData();
    }
  }, [user?.id, isAuthenticated]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle authentication required
  const handleAuthRequired = () => {
    navigate(buildSignInRedirect(`${window.location.pathname}${window.location.search}`));
  };

  const formatTrustScore = (score: number) => {
    if (score >= 1000) {
      return `${(score / 1000).toFixed(score % 1000 === 0 ? 0 : 1)}K`;
    }
    return score.toString();
  };

  const getLevelColor = () => {
    switch (level) {
      case 'bronze': return 'text-amber-500';
      case 'silver': return 'text-gray-300';
      case 'gold': return 'text-yellow-400';
      case 'diamond': return 'text-blue-300';
      case 'legend': return 'text-purple-500';
      default: return 'text-gray-300';
    }
  };

  const getLevelIcon = () => {
    switch (level) {
      case 'bronze': return <Star className="h-3.5 w-3.5" />;
      case 'silver': return <Star className="h-3.5 w-3.5" />;
      case 'gold': return <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />;
      case 'diamond': return <Gem className="h-3.5 w-3.5" />;
      case 'legend': return <BadgeCheck className="h-3.5 w-3.5" />;
      default: return <Star className="h-3.5 w-3.5" />;
    }
  };

  const menuItems = [
    {
      icon: User,
      label: "Profile",
      path: `/profile/${user?.id}`,
      onClick: () => {
        setIsProfileOpen(false);
        setIsMobileMenuOpen(false);
      },
      authRequired: true
    },
    {
      icon: Compass,
      label: "Explore",
      path: "/explore",
      onClick: () => {
        setIsProfileOpen(false);
        setIsMobileMenuOpen(false);
      },
      authRequired: false
    },
    {
      icon: Shield,
      label: "Privacy Policy",
      path: "/privacy-policies",
      onClick: () => {
        setIsProfileOpen(false);
        setIsMobileMenuOpen(false);
      },
      authRequired: false
    },
    {
      icon: FileText,
      label: "Terms & Conditions",
      path: "/termandcondition",
      onClick: () => {
        setIsProfileOpen(false);
        setIsMobileMenuOpen(false);
      },
      authRequired: false
    },
    {
      icon: HelpCircle,
      label: "FAQ",
      path: "/faq",
      onClick: () => {
        setIsProfileOpen(false);
        setIsMobileMenuOpen(false);
      },
      authRequired: false
    },
    {
      icon: BookOpen,
      label: "User Guide",
      path: "/busiconn-guide",
      onClick: () => {
        setIsProfileOpen(false);
        setIsMobileMenuOpen(false);
      },
      authRequired: false
    }
  ];

  const handleSignOut = () => {
    setIsProfileOpen(false);
    setIsMobileMenuOpen(false);
    signOut();
  };

  const handleMenuItemClick = (item: any) => {
    if (item.authRequired && !isAuthenticated) {
      handleAuthRequired();
      return;
    }
    // For non-auth required items, navigate directly
    if (!item.authRequired) {
      navigate(item.path);
      setIsMobileMenuOpen(false);
      return;
    }
    item.onClick();
  };

  return (
    <section
      className={`topbar transition-transform duration-300 sticky top-0 w-full z-50 bg-dark-2 ${
        scrollDir === "down" ? "-translate-y-full" : "translate-y-0"
      }`}
    >
      <div className="flex-between py-4 px-5">
        <Link to="/home" className="flex gap-3 items-center">
          <img
            src="/assets/images/busiconn.svg"
            alt="logo"
            className="w-28 md:w-36"
          />
        </Link>

        <div className="flex gap-4 items-center">
          {/* Desktop: TrustScore Badge + Notifications + Profile */}
          <div className="hidden md:flex items-center gap-4">
            {/* TrustScore Badge - Only show if authenticated */}
            {isAuthenticated && trustScore !== null && (
              <button
                type="button"
                onClick={() => navigate("/trust-score")}
                aria-label="Open TrustScore dashboard"
                className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-800 rounded-full hover:bg-gray-700 transition-colors"
              >
                <span className="text-xs font-medium text-light-3">TS:</span>
                <span className="text-sm font-semibold text-light-1">
                  {formatTrustScore(trustScore)}
                </span>
                <span className={`${getLevelColor()}`}>
                  {getLevelIcon()}
                </span>
              </button>
            )}

            {/* Notification bell - Only show if authenticated */}
            {isAuthenticated && <NotificationDropdown />}

            {/* Profile Dropdown or Auth Buttons */}
            {isAuthenticated ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="flex items-center gap-2 p-1 rounded-full hover:bg-dark-3 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-dark-2"
                >
                  <img
                    src={user?.imageUrl || "/assets/icons/profile-placeholder.svg"}
                    alt="profile"
                    className="h-8 w-8 rounded-full object-cover border-2 border-transparent hover:border-primary-500 transition-all duration-200"
                  />
                  <ChevronDown 
                    className={`h-4 w-4 text-light-3 transition-transform duration-200 ${
                      isProfileOpen ? "rotate-180" : ""
                    }`} 
                  />
                </button>

                {/* Desktop Profile Dropdown */}
                {isProfileOpen && (
                  <div className="absolute right-0 mt-3 w-72 bg-dark-1 border border-dark-4 rounded-xl shadow-2xl overflow-hidden z-50 animate-in slide-in-from-top-2 duration-200">
                    {/* User Info Header */}
                    <div className="px-4 py-4 bg-gradient-to-r from-primary-500/10 to-purple-500/10 border-b border-dark-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={user?.imageUrl || "/assets/icons/profile-placeholder.svg"}
                          alt="profile"
                          className="h-12 w-12 rounded-full object-cover border-2 border-primary-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-light-1 font-semibold truncate">
                            {user?.name || "User"}
                          </p>
                          <p className="text-light-3 text-sm truncate">
                            {formatLoginIdentifier(user?.emailOrPhone)}
                          </p>
                          {trustScore !== null && (
                            <div className="flex items-center mt-1 space-x-2">
                              <div className="flex items-center gap-1 px-2 py-0.5 bg-dark-3 rounded-full text-xs">
                                <span className="text-light-3">TS:</span>
                                <span className="font-semibold text-light-1">
                                  {formatTrustScore(trustScore)}
                                </span>
                                <span className={`${getLevelColor()}`}>
                                  {getLevelIcon()}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    <div className="py-2">
                      {menuItems.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          onClick={item.onClick}
                          className="flex items-center gap-3 px-4 py-3 text-light-2 hover:bg-dark-3 hover:text-light-1 transition-all duration-200 group"
                        >
                          <item.icon className="h-5 w-5 text-light-3 group-hover:text-primary-500 transition-colors duration-200" />
                          <span className="font-medium">{item.label}</span>
                        </Link>
                      ))}
                      
                      {/* TrustScore Link */}
                      <Link
                        to="/trust-score"
                        onClick={() => setIsProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-light-2 hover:bg-dark-3 hover:text-light-1 transition-all duration-200 group"
                      >
                        <div className="h-5 w-5 flex items-center justify-center text-light-3 group-hover:text-primary-500 transition-colors duration-200">
                          {getLevelIcon()}
                        </div>
                        <span className="font-medium">TrustScore Dashboard</span>
                      </Link>
                      
                      {/* Contact Us Section */}
                      <div className="px-4 py-3 border-t border-dark-4 mt-2">
                        <div className="flex items-center gap-3 mb-2">
                          <Mail className="h-5 w-5 text-primary-500" />
                          <span className="font-medium text-light-1">Contact Us</span>
                        </div>
                        <div className="ml-8">
                          <a 
                            href="mailto:contact@busiconn.com"
                            className="text-light-3 hover:text-primary-500 transition-colors duration-200 text-sm font-medium"
                          >
                            contact@busiconn.com
                          </a>
                        </div>
                      </div>
                      
                      {/* Divider */}
                      <div className="border-t border-dark-4 my-2"></div>
                      
                      {/* Logout Button */}
                      <button
                        onClick={handleSignOut}
                        className="flex items-center gap-3 px-4 py-3 text-red hover:bg-orange-200 hover:text-orange-400 transition-all duration-200 w-full group"
                      >
                        <LogOut className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                        <span className="font-medium">Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Desktop Auth Buttons */
              <div className="flex items-center gap-2">
                <button
                  onClick={handleAuthRequired}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-light-2 hover:text-light-1 transition-colors duration-200"
                >
                  <LogIn className="h-4 w-4" />
                  Sign In
                </button>
                <button
                  onClick={() => navigate('/sign-up')}
                  className="flex items-center gap-2 px-3 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                >
                  <UserPlus className="h-4 w-4" />
                  Join Free
                </button>
              </div>
            )}
          </div>

          {/* Mobile: TrustScore + Notifications + Hamburger Menu */}
          <div className="md:hidden flex items-center gap-2">
            {/* Mobile TrustScore Badge - Only show if authenticated */}
            {isAuthenticated && trustScore !== null && (
              <button
                type="button"
                onClick={() => navigate("/trust-score")}
                aria-label="Open TrustScore dashboard"
                className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-800 rounded-full hover:bg-gray-700 transition-colors"
              >
                <span className="text-xs font-medium text-light-3">TS:</span>
                <span className="text-sm font-semibold text-light-1">
                  {formatTrustScore(trustScore)}
                </span>
                <span className={`${getLevelColor()}`}>
                  {getLevelIcon()}
                </span>
              </button>
            )}

            {/* Mobile Notification bell - Only show if authenticated */}
            {isAuthenticated && <NotificationDropdown />}

            {/* Hamburger Menu */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-light-2 hover:text-light-1 transition-colors duration-200"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-dark-1 border-t border-dark-4 shadow-lg z-50">
          <div className="px-4 py-4">
            {/* Mobile User Info - Only show if authenticated */}
            {isAuthenticated && (
              <div className="mb-4 p-3 bg-gradient-to-r from-primary-500/10 to-purple-500/10 rounded-lg">
                <div className="flex items-center gap-3">
                  <img
                    src={user?.imageUrl || "/assets/icons/profile-placeholder.svg"}
                    alt="profile"
                    className="h-10 w-10 rounded-full object-cover border-2 border-primary-500"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-light-1 font-semibold truncate">
                      {user?.name || "User"}
                    </p>
                    <p className="text-light-3 text-sm truncate">
                      {formatLoginIdentifier(user?.emailOrPhone)}
                    </p>
                    {trustScore !== null && (
                      <div className="flex items-center mt-1">
                        <div className="flex items-center gap-1 px-2 py-0.5 bg-dark-3 rounded-full text-xs">
                          <span className="text-light-3">TS:</span>
                          <span className="font-semibold text-light-1">
                            {formatTrustScore(trustScore)}
                          </span>
                          <span className={`${getLevelColor()}`}>
                            {getLevelIcon()}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Mobile Auth Buttons - Only show if not authenticated */}
            {!isAuthenticated && (
              <div className="mb-4 space-y-2">
                <button
                  onClick={handleAuthRequired}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 text-light-2 hover:text-light-1 hover:bg-dark-3 rounded-lg transition-colors duration-200"
                >
                  <LogIn className="h-4 w-4" />
                  Sign In
                </button>
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    navigate('/sign-up');
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors duration-200"
                >
                  <UserPlus className="h-4 w-4" />
                  Join Free
                </button>
              </div>
            )}

            {/* Mobile Menu Items */}
            <div className="space-y-1">
              {menuItems.map((item, index) => (
                item.authRequired && !isAuthenticated ? (
                  <button
                    key={index}
                    onClick={() => handleMenuItemClick(item)}
                    className="w-full flex items-center justify-between px-4 py-3 text-light-2 hover:bg-dark-3 hover:text-light-1 rounded-lg transition-all duration-200 group"
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className="h-5 w-5 text-light-3 group-hover:text-primary-500 transition-colors duration-200" />
                      <span className="font-medium">{item.label}</span>
                    </div>
                    <img src="/assets/icons/lock.svg" alt="locked" width={12} height={12} />
                  </button>
                ) : (
                  <Link
                    key={index}
                    to={item.path}
                    onClick={() => handleMenuItemClick(item)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-light-2 hover:bg-dark-3 hover:text-light-1 rounded-lg transition-all duration-200 group"
                  >
                    <item.icon className="h-5 w-5 text-light-3 group-hover:text-primary-500 transition-colors duration-200" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                )
              ))}
              
              {/* TrustScore Link */}
              {isAuthenticated ? (
                <Link
                  to="/trust-score"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-light-2 hover:bg-dark-3 hover:text-light-1 rounded-lg transition-all duration-200 group"
                >
                  <div className="h-5 w-5 flex items-center justify-center text-light-3 group-hover:text-primary-500 transition-colors duration-200">
                    {getLevelIcon()}
                  </div>
                  <span className="font-medium">TrustScore Dashboard</span>
                </Link>
              ) : (
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    handleAuthRequired();
                  }}
                  className="w-full flex items-center justify-between px-4 py-3 text-light-2 hover:bg-dark-3 hover:text-light-1 rounded-lg transition-all duration-200 group"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-5 w-5 flex items-center justify-center text-light-3 group-hover:text-primary-500 transition-colors duration-200">
                      <Star className="h-4 w-4" />
                    </div>
                    <span className="font-medium">TrustScore Dashboard</span>
                  </div>
                  <img src="/assets/icons/lock.svg" alt="locked" width={12} height={12} />
                </button>
              )}
              
              {/* Contact Us Section */}
              <div className="px-4 py-3 border-t border-dark-4 mt-2">
                <div className="flex items-center gap-3 mb-2">
                  <Mail className="h-5 w-5 text-primary-500" />
                  <span className="font-medium text-light-1">Contact Us</span>
                </div>
                <div className="ml-8">
                  <a 
                    href="mailto:contact@busiconn.com"
                    className="text-light-3 hover:text-primary-500 transition-colors duration-200 text-sm font-medium"
                  >
                    contact@busiconn.com
                  </a>
                </div>
              </div>
              
              {/* Logout Button - Only show if authenticated */}
              {isAuthenticated && (
                <>
                  <div className="border-t border-dark-4 my-2"></div>
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-3 px-4 py-3 text-red hover:bg-orange-200 hover:text-orange-400 rounded-lg transition-all duration-200 group"
                  >
                    <LogOut className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                    <span className="font-medium">Sign Out</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Topbar;
